import { writable } from 'svelte/store'
import { push } from 'svelte-spa-router'

/**
 * True while any form in the app (currently: the filter forms in
 * FiltersView) has unsaved edits. Drives the native beforeunload warning,
 * the in-app Back/Forward guard below, and the app-wide link interceptor —
 * so the sidebar and other internal links need no per-link wiring.
 */
export const hasUnsavedChanges = writable(false)

/** True while the "leave without saving?" modal should be shown. */
export const pendingLeaveConfirm = writable(false)

let currentDirty = false
let guardDepth = 0
let ignorePopstate = false
let onLeaveConfirmed: (() => void) | null = null

function beforeUnloadHandler(event: BeforeUnloadEvent): void {
  event.preventDefault()
  event.returnValue = ''
}

function pushGuardEntry(): void {
  history.pushState(history.state, '', window.location.href)
}

/**
 * Pressing Back/Forward while dirty doesn't trigger beforeunload (no
 * document unload happens for a same-document hash change), so it needs its
 * own guard: a duplicate history entry is kept on top while dirty. Landing
 * on it via popstate means the user just tried to leave — re-plant the
 * duplicate (so the app doesn't actually move) and ask; confirming jumps
 * back over every duplicate accumulated this way plus the original attempt.
 */
function onPopState(): void {
  if (ignorePopstate) {
    ignorePopstate = false
    return
  }
  if (!currentDirty) return

  pushGuardEntry()
  guardDepth += 1

  openLeaveModal(() => {
    currentDirty = false
    hasUnsavedChanges.set(false)
    window.removeEventListener('beforeunload', beforeUnloadHandler)
    const depth = guardDepth
    guardDepth = 0
    ignorePopstate = true
    history.go(-depth)
  })
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', onPopState)
}

/**
 * Intercepts clicks on in-app links (sidebar, header, anywhere using
 * svelte-spa-router's `use:link`) while a form is dirty, in the capture
 * phase — before `use:link`'s own click handler on the anchor itself runs
 * and navigates. Modifier/middle clicks (open in new tab, etc.) are left
 * alone.
 */
function onDocumentClickCapture(event: MouseEvent): void {
  if (!currentDirty) return
  if (event.defaultPrevented) return
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

  const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]')
  if (!anchor) return
  const href = anchor.getAttribute('href')
  if (!href || !(href.startsWith('/') || href.startsWith('#/'))) return

  event.preventDefault()
  event.stopPropagation()
  requestLeave(() => {
    // Navigating away, not staying on this route — clear the in-memory flag
    // but leave any guard history entries where they are. Popping them here
    // (history.go) would step backwards through the very navigation the
    // user just confirmed; they're harmless clutter left behind instead.
    currentDirty = false
    hasUnsavedChanges.set(false)
    window.removeEventListener('beforeunload', beforeUnloadHandler)
    guardDepth = 0
    void push(href)
  })
}

if (typeof window !== 'undefined') {
  document.addEventListener('click', onDocumentClickCapture, true)
}

/**
 * Marks whether the active form has unsaved edits. Call with `true` as soon
 * as the form differs from its saved baseline, and `false` once it's saved,
 * cancelled, or reset — as long as the caller is staying on the same route
 * (clearing to `false` pops any guard history entry pushed while dirty). A
 * confirmed navigation away clears the flag itself instead, without popping.
 */
export function setUnsavedChanges(dirty: boolean): void {
  if (dirty === currentDirty) return
  currentDirty = dirty
  hasUnsavedChanges.set(dirty)

  if (dirty) {
    window.addEventListener('beforeunload', beforeUnloadHandler)
    pushGuardEntry()
    guardDepth = 1
  } else {
    window.removeEventListener('beforeunload', beforeUnloadHandler)
    if (guardDepth > 0) {
      const depth = guardDepth
      guardDepth = 0
      ignorePopstate = true
      history.go(-depth)
    }
  }
}

/** Runs `onConfirm` immediately if nothing is dirty; otherwise shows the
 * "leave without saving?" modal and runs it only if the user confirms. */
export function requestLeave(onConfirm: () => void): void {
  if (!currentDirty) {
    onConfirm()
    return
  }
  openLeaveModal(onConfirm)
}

function openLeaveModal(onConfirm: () => void): void {
  onLeaveConfirmed = onConfirm
  pendingLeaveConfirm.set(true)
}

export function confirmLeave(): void {
  const fn = onLeaveConfirmed
  onLeaveConfirmed = null
  pendingLeaveConfirm.set(false)
  fn?.()
}

export function cancelLeave(): void {
  onLeaveConfirmed = null
  pendingLeaveConfirm.set(false)
}
